import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Paper,
  Box,
  Avatar,
  Chip,
  Grid,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import AvatarUpload from '../components/AvatarUpload';
import { useFavorites } from '../context/FavoritesContext';
import { useWatchHistory } from '../context/WatchHistoryContext';
import {
  AccountCircle as AccountCircleIcon,
  Favorite as FavoriteIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const Profile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const { favorites, removeFavorite } = useFavorites();
  const { watchHistory, removeFromWatchHistory } = useWatchHistory();
  
  const [editMode, setEditMode] = useState(false);
  const [userInfo, setUserInfo] = useState({
    username: 'testuser',
    email: 'test@example.com',
    bio: '这是一个测试用户的个人简介',
    joinDate: '2026-01-01',
    favorites: 0,
    history: 0,
    avatar: '', // 添加头像字段
  });

  // 简化的认证检查：只检查localStorage，不做多余的跳转
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    console.log('Profile 页面加载，localStorage.user:', storedUser ? '存在' : '不存在');
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log('解析后的用户数据:', user);
        
        if (user && (user.email || user.username)) {
          // 数据有效，直接设置用户信息
          setUserInfo(prev => ({
            ...prev,
            email: user.email || prev.email,
            username: user.username || user.email?.split('@')[0] || prev.username,
            avatar: user.avatar || '',
            joinDate: user.created_at ? new Date(user.created_at).toLocaleDateString() : 
                      user.joinDate || '2026-01-01',
          }));
          console.log('用户信息设置成功');
        } else {
          console.error('用户数据格式无效，跳转到登录页');
          history.push('/login');
        }
      } catch (error) {
        console.error('解析用户数据失败:', error);
        history.push('/login');
      }
    } else {
      console.error('localStorage 中没有用户数据，跳转到登录页');
      history.push('/login');
    }
  }, []); // 只在组件挂载时执行一次

  // 更新观看历史统计数据
  useEffect(() => {
    setUserInfo(prev => ({
      ...prev,
      favorites: favorites.length,
      history: watchHistory.length,
    }));
  }, [favorites.length, watchHistory.length]);

  // 处理头像更新
  const handleAvatarUpdate = (avatarUrl) => {
    setUserInfo(prev => ({
      ...prev,
      avatar: avatarUrl,
    }));
    
    // 更新本地存储中的用户信息
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const updatedUser = { ...user, avatar: avatarUrl };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        console.error('更新用户信息失败:', error);
      }
    }
  };

  const handleEdit = () => {
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    try {
      // 从本地存储获取用户ID
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        alert('用户未登录');
        return;
      }
      
      const user = JSON.parse(storedUser);
      // 尝试多种可能的ID字段名
      const userId = user._id || user.id || user.userId;
      
      if (!userId) {
        console.error('用户数据中没有ID字段，用户数据:', user);
        alert('无法获取用户ID，请重新登录');
        return;
      }
      
      // 准备要发送的数据
      const updateData = {
        username: userInfo.username,
        email: userInfo.email,
        bio: userInfo.bio,
        avatar: userInfo.avatar,
      };
      
      console.log('更新用户资料，用户ID:', userId, '数据:', updateData);
      
      // 调用API更新用户资料 - 先尝试旧的API
      try {
        const response = await fetch(`/api/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });
        
        if (response.ok) {
          // 更新本地存储中的用户信息
          const updatedUser = { ...user, ...updateData };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setEditMode(false);
          alert('个人资料更新成功！');
          return;
        } else {
          console.log('旧API更新失败，状态码:', response.status);
        }
      } catch (apiError) {
        console.log('旧API更新失败，尝试MongoDB API:', apiError.message);
      }
      
      // 如果旧API失败，尝试MongoDB的v2 API
      try {
        await axios.put(`/api/v2/users/${userId}/profile`, updateData);
        // 更新本地存储中的用户信息
        const updatedUser = { ...user, ...updateData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setEditMode(false);
        alert('个人资料更新成功！');
      } catch (mongoError) {
        console.error('MongoDB API也失败了:', mongoError);
        alert('更新失败，请稍后重试');
      }
    } catch (error) {
      console.error('更新个人资料失败:', error);
      alert('更新失败: ' + error.message);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 2 }}>
      {/* 用户信息卡片 */}
      <Paper sx={{ 
        p: isMobile ? 2 : 4, 
        mb: 4, 
        borderRadius: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start', mb: 4 }}>
          <Avatar
            src={userInfo.avatar}
            sx={{
              width: isMobile ? 80 : 120,
              height: isMobile ? 80 : 120,
              mr: isMobile ? 0 : 4,
              mb: isMobile ? 2 : 0,
              border: '4px solid rgba(255, 255, 255, 0.2)',
              boxShadow: 3
            }}
          >
            {!userInfo.avatar && <AccountCircleIcon sx={{ fontSize: isMobile ? 40 : 64 }} />}
          </Avatar>
          <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'center' : 'left' }}>
            <Typography 
              variant={isMobile ? 'h5' : 'h4'} 
              component="h1" 
              sx={{ mb: 1, fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: 'bold' }}
            >
              {userInfo.username}
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 2, opacity: 0.9 }}>
              {userInfo.email}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <Chip
                icon={<FavoriteIcon />}
                label={`${userInfo.favorites}个收藏`}
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  fontSize: isMobile ? '0.75rem' : '1rem',
                  '& .MuiChip-icon': {
                    color: 'white'
                  }
                }}
              />
              <Chip
                icon={<HistoryIcon />}
                label={`${userInfo.history}个历史记录`}
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  fontSize: isMobile ? '0.75rem' : '1rem',
                  '& .MuiChip-icon': {
                    color: 'white'
                  }
                }}
              />
              <Chip
                label={`加入日期: ${userInfo.joinDate}`}
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  fontSize: isMobile ? '0.75rem' : '1rem'
                }}
              />
            </Box>
            <Tooltip title={editMode ? '取消编辑' : '编辑信息'}>
              <IconButton 
                color="inherit"
                onClick={handleEdit}
                sx={{ mt: 2 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 用户信息表单 */}
        {editMode && (
          <Paper sx={{ p: isMobile ? 2 : 3, mt: 3, borderRadius: 1, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            {/* 头像上传 */}
            <AvatarUpload
              currentAvatar={userInfo.avatar}
              onAvatarUpdate={handleAvatarUpdate}
              isMobile={isMobile}
            />
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="用户名"
                  value={userInfo.username}
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, username: e.target.value })
                  }
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="邮箱"
                  type="email"
                  value={userInfo.email}
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, email: e.target.value })
                  }
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="个人简介"
                  multiline
                  rows={isMobile ? 2 : 3}
                  value={userInfo.bio}
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, bio: e.target.value })
                  }
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleEdit}
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                取消
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSave}
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                保存
              </Button>
            </Box>
          </Paper>
        )}

        {/* 个人简介 */}
        <Paper sx={{ 
          p: isMobile ? 2 : 3, 
          mt: 4, 
          borderRadius: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography 
            variant={isMobile ? 'h6' : 'h5'} 
            component="h3" 
            sx={{ mb: 2, fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 'bold' }}
          >
            个人简介
          </Typography>
          <Typography 
            variant="body1"
            fontSize={isMobile ? '0.875rem' : '1rem'}
            sx={{ opacity: 0.9 }}
          >
            {userInfo.bio}
          </Typography>
        </Paper>
      </Paper>

      {/* 收藏和历史记录 */}
      <Grid container spacing={isMobile ? 2 : 4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: isMobile ? 2 : 3, 
            borderRadius: 2,
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <FavoriteIcon sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', mr: 1, color: '#e91e63' }} />
              <Typography 
                variant={isMobile ? 'h6' : 'h5'} 
                component="h3" 
                sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 'bold' }}
              >
                我的收藏
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {favorites.length > 0 ? (
                favorites.slice(0, 100).map((favorite, index) => (
                  <Paper
                    key={favorite.id}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: 2
                      }
                    }}
                    onClick={() => history.push(`/drama/${favorite.id}`)}
                  >
                    <img
                      src={favorite.poster}
                      alt={favorite.title}
                      style={{ 
                        width: isMobile ? 70 : 100, 
                        height: isMobile ? 50 : 70, 
                        objectFit: 'cover', 
                        mr: 2,
                        borderRadius: '4px'
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        variant={isMobile ? 'body1' : 'h6'}
                        fontSize={isMobile ? '0.875rem' : '1rem'}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {favorite.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        fontSize={isMobile ? '0.75rem' : '0.875rem'}
                      >
                        {favorite.views}次观看
                      </Typography>
                    </Box>
                    <Tooltip title="取消收藏">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFavorite(favorite.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))
              ) : (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <FavoriteIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    您还没有收藏任何剧集
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => history.push('/')}
                  >
                    去浏览剧集
                  </Button>
                </Paper>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: isMobile ? 2 : 3, 
            borderRadius: 2,
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <HistoryIcon sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', mr: 1, color: '#ff9800' }} />
              <Typography 
                variant={isMobile ? 'h6' : 'h5'} 
                component="h3" 
                sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 'bold' }}
              >
                观看历史
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {watchHistory.length > 0 ? (
                watchHistory.slice(0, 100).map((item, index) => (
                  <Paper
                    key={item.id}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: 2
                      }
                    }}
                    onClick={() => history.push(`/drama/${item.id}`)}
                  >
                    <img
                      src={item.poster}
                      alt={item.title}
                      style={{ 
                        width: isMobile ? 70 : 100, 
                        height: isMobile ? 50 : 70, 
                        objectFit: 'cover', 
                        mr: 2,
                        borderRadius: '4px'
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        variant={isMobile ? 'body1' : 'h6'}
                        fontSize={isMobile ? '0.875rem' : '1rem'}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {item.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        fontSize={isMobile ? '0.75rem' : '0.875rem'}
                      >
                        {new Date(item.watchTime).toLocaleString()}
                      </Typography>
                    </Box>
                    <Tooltip title="删除历史记录">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFromWatchHistory(item.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))
              ) : (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    您还没有观看过任何剧集
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => history.push('/')}
                  >
                    去浏览剧集
                  </Button>
                </Paper>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 设置 */}
      <Paper sx={{ 
        p: isMobile ? 2 : 4, 
        borderRadius: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.02)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SettingsIcon sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', mr: 1, color: '#2196f3' }} />
          <Typography 
            variant={isMobile ? 'h6' : 'h5'} 
            component="h3" 
            sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 'bold' }}
          >
            设置
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'rgba(33, 150, 243, 0.05)',
              borderRadius: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                transform: 'translateX(4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 2 }} />
              <Typography 
                variant="body1"
                fontSize={isMobile ? '0.875rem' : '1rem'}
              >
                账户设置
              </Typography>
            </Box>
            <IconButton 
              size="small"
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'rgba(33, 150, 243, 0.05)',
              borderRadius: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                transform: 'translateX(4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 2 }} />
              <Typography 
                variant="body1"
                fontSize={isMobile ? '0.875rem' : '1rem'}
              >
                通知设置
              </Typography>
            </Box>
            <IconButton 
              size="small"
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'rgba(33, 150, 243, 0.05)',
              borderRadius: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                transform: 'translateX(4px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 2 }} />
              <Typography 
                variant="body1"
                fontSize={isMobile ? '0.875rem' : '1rem'}
              >
                隐私设置
              </Typography>
            </Box>
            <IconButton 
              size="small"
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;
